import React, { useState } from 'react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts'
import { Box, Typography, useTheme } from '@mui/material'
import EmptyScreen from 'packages/ui/EmptyScreen/EmptyScreen'
import WidgetPieChartSkeleton from './Skeletons/WidgetPieChartSkeleton'
import {
  StyledWidgetPieChartStatistic,
  CustomLegend,
  CustomTooltip
} from './WidgetPieChartStatistic.styles'

interface PieChartData {
  name: string
  value: number
  color: string
}

interface WidgetPieChartStatisticProps {
  data: PieChartData[]
  title?: string
  loading?: boolean
}

interface CustomTooltipPayload {
  name: string
  value: number
  percent: number
  color: string
  payload: PieChartData
}

const WidgetPieChartStatistic: React.FC<WidgetPieChartStatisticProps> = ({ data, title, loading = false }) => {
  const theme = useTheme()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  if (loading) return <WidgetPieChartSkeleton />
  if (!data || data.length === 0) {
    return <EmptyScreen icon="square-poll-vertical">No data available</EmptyScreen>
  }

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index)
    setIsTooltipVisible(true)
  }

  const handleMouseLeave = () => {
    setHoveredIndex(null)
    setIsTooltipVisible(false)
  }

  const formatNumber = (value: number) => {
    const formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
    return formatter.format(value).replace(/,/g, ' ')
  }

  const renderTooltip = ({ active, payload, coordinate }: TooltipProps<number, string>) => {
    if (active && payload && payload.length && isTooltipVisible) {
      const { name, value, percent, color } = payload[0].payload as CustomTooltipPayload
      const tooltipStyle = {
        top: coordinate?.y ? coordinate.y - 120 : 0,
        left: coordinate?.x ? coordinate.x - 20 : 0,
      }

      return (
        <CustomTooltip style={tooltipStyle}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ width: '10px', height: '10px', backgroundColor: color, mr: 1, position: 'relative', top: '5px' }} />
            <Typography variant="subtitle2">{name}</Typography>
          </Box>
          <Box sx={{ position: 'absolute', bottom: theme.spacing(1), right: theme.spacing(1) }}>
            <Typography variant="body2" color="textSecondary">
              {percent.toLocaleString()} - {formatNumber(value)}
            </Typography>
          </Box>
        </CustomTooltip>
      )
    }
    return null
  }

  return (
    <StyledWidgetPieChartStatistic>
      <Typography variant="h6" fontWeight={500} width={'100%'} marginBottom={{ xs: 1, sm: 2 }} color={theme.palette.text.primary}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }}>
        <Box sx={{ flexShrink: 0, width: { xs: 80, sm: 140 }, height: { xs: 80, sm: 140 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart
              onMouseEnter={() => setIsTooltipVisible(true)}
              onMouseLeave={() => setIsTooltipVisible(false)}
            >
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="100%"
                fill="#8884d8"
                paddingAngle={0.5}
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => {
                  setIsTooltipVisible(false)
                  handleMouseLeave()
                }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.5}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  />
                ))}
              </Pie>
              <Tooltip wrapperStyle={{ zIndex: 2 }} content={renderTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <CustomLegend sx={{ flex: 1, minWidth: 0, ml: 2 }}>
          {data.map((entry, index) => (
            <div
              key={`legend-${index}`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              style={{
                opacity: hoveredIndex === null ? 1 : hoveredIndex === index ? 1 : 0.5,
                cursor: 'pointer',
              }}
            >
              <span style={{ backgroundColor: entry.color }}></span>
              <Typography component="p">{entry.name}</Typography>
              <Typography component="p">
                {formatNumber(entry.value)}
              </Typography>
            </div>
          ))}
        </CustomLegend>
      </Box>
    </StyledWidgetPieChartStatistic>
  )
}

export default WidgetPieChartStatistic
